/**
 * PaginationBar
 *
 * Paginador compartido para las vistas de lista.
 *
 * Por qué existe: varias vistas (las 4 de /tareas, /inventario) pedían al
 * servicio la página 0 con el `pageSize` por defecto de 20 y NO renderizaban
 * ningún control. El resultado era un tope invisible: 20 de 190 tareas, 20 de 54
 * artículos, sin nada en pantalla que dijera que había más. El usuario no ve una
 * página 1 de 10; ve "esto es todo lo que hay".
 *
 * Reglas de uso:
 * - `totalCount` tiene que ser el conteo del conjunto YA FILTRADO. Si se filtra
 *   después de paginar, este componente miente igual que el silencio anterior.
 * - Siempre se renderiza el resumen "Mostrando X–Y de N", incluso con una sola
 *   página: el conteo es la información que faltaba, no solo los botones.
 *
 * @module components/common/PaginationBar
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Form } from 'react-bootstrap';

export const PAGE_SIZE_OPTIONS = [20, 50, 100];

const PaginationBar = ({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'resultados',
  className = '',
}) => {
  const total = totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : page * pageSize + 1;
  const last = Math.min(total, (page + 1) * pageSize);

  const goTo = (nextPage) => {
    if (nextPage < 0 || nextPage > totalPages - 1) return;
    onPageChange(nextPage);
  };

  return (
    <div
      className={`d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mt-3 ${className}`}
      data-testid="pagination-bar"
    >
      <div className="text-muted small" data-testid="pagination-summary">
        Mostrando {first}-{last} de {total} {itemLabel}
      </div>

      <div className="d-flex align-items-center gap-2">
        {onPageSizeChange && (
          <Form.Select
            size="sm"
            style={{ width: 'auto' }}
            aria-label="Resultados por página"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </Form.Select>
        )}

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => goTo(page - 1)}
          disabled={page === 0}
        >
          <i className="bi bi-chevron-left"></i> Anterior
        </Button>
        <span className="text-muted small text-nowrap">
          Página {page + 1} de {totalPages}
        </span>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => goTo(page + 1)}
          disabled={page >= totalPages - 1}
        >
          Siguiente <i className="bi bi-chevron-right"></i>
        </Button>
      </div>
    </div>
  );
};

PaginationBar.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  /** Conteo del conjunto YA FILTRADO (no el de la tabla entera). */
  totalCount: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func,
  itemLabel: PropTypes.string,
  className: PropTypes.string,
};

export default PaginationBar;
